import { useEffect, useState } from 'react'
import { Avatar, Chip, Button, KpiCard } from '@/components/ui'
import { ChevronRight, Pause, Play, Zap, MessageCircle, CheckCircle2 } from 'lucide-react'
import { cn, fmtDateTime } from '@/lib/utils'

interface FeedEvent {
  id: string
  cliente: string
  tipo: 'consulta' | 'respuesta' | 'escalado' | 'resuelto'
  mensaje: string
  intent?: string
  fecha: Date
  wa: string
}

const eventosIniciales: FeedEvent[] = [
  { id: 'e1', cliente: 'MIELES CEDEÑO LIGIA', wa: '+593 99 XXX 2466', tipo: 'consulta', mensaje: 'cuanto debo', fecha: new Date(Date.now() - 1000 * 90) },
  { id: 'e2', cliente: 'MIELES CEDEÑO LIGIA', wa: '+593 99 XXX 2466', tipo: 'respuesta', intent: 'consultar_saldo', mensaje: 'Hola Ligia. Tu saldo vencido es $59.74 por el crédito #1869275 (Refrigeradora).', fecha: new Date(Date.now() - 1000 * 85) },
  { id: 'e3', cliente: 'GREFA SALAZAR DANNY', wa: '+593 99 XXX 7640', tipo: 'consulta', mensaje: 'donde puedo pagar', fecha: new Date(Date.now() - 1000 * 55) },
  { id: 'e4', cliente: 'GREFA SALAZAR DANNY', wa: '+593 99 XXX 7640', tipo: 'respuesta', intent: 'ver_cuentas', mensaje: '9 cuentas Point: Pichincha 3077712404 · Guayaquil 41205385 · ProduBanco 02002031200 · …', fecha: new Date(Date.now() - 1000 * 48) },
  { id: 'e5', cliente: 'MOREIRA SOLÓRZANO JUAN', wa: '+593 99 XXX 1123', tipo: 'resuelto', mensaje: 'Cliente resolvió consulta de saldo + confirmó próximo pago', intent: 'cierre_bot', fecha: new Date(Date.now() - 1000 * 30) },
  { id: 'e6', cliente: 'VERA LOOR PEDRO', wa: '+593 99 XXX 3321', tipo: 'escalado', mensaje: 'Cliente pide descuento no estándar → cola humana', intent: 'escalacion_descuento', fecha: new Date(Date.now() - 1000 * 15) },
]

const tipoConfig: Record<FeedEvent['tipo'], { label: string; chip: string; dot: string; icon: typeof MessageCircle }> = {
  consulta: { label: 'IN', chip: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400', icon: MessageCircle },
  respuesta: { label: 'OUT', chip: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500', icon: Zap },
  escalado: { label: 'ESC', chip: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500', icon: ChevronRight },
  resuelto: { label: 'OK', chip: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500', icon: CheckCircle2 },
}

export function Supervision() {
  const [paused, setPaused] = useState(false)
  const [eventos, setEventos] = useState(eventosIniciales)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (paused) return
    const int = setInterval(() => setTick(t => t + 1), 5000)
    return () => clearInterval(int)
  }, [paused])

  useEffect(() => {
    if (tick === 0) return
    const nuevo: FeedEvent = {
      id: `e${Date.now()}`,
      cliente: 'CLIENTE DEMO ' + (100 + Math.floor(Math.random() * 900)),
      wa: '+593 99 XXX ' + (1000 + Math.floor(Math.random() * 8999)),
      tipo: (['consulta', 'respuesta', 'resuelto'] as const)[Math.floor(Math.random() * 3)],
      mensaje: 'Evento auto-generado · ' + new Date().toLocaleTimeString('es-EC'),
      fecha: new Date(),
    }
    setEventos(prev => [nuevo, ...prev].slice(0, 20))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick])

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Mensajes última hora"
          value={eventos.length}
          sub="in + out"
          icon={<span className="pulse-live" />}
          color="emerald"
        />
        <KpiCard label="Intents detectados" value="7" sub="únicos hoy" />
        <KpiCard label="Tasa auto-resolución" value="90%" sub="2 escalados / 38 bot" color="emerald" />
        <KpiCard label="Latencia prom. OUT" value="2.3s" sub="LLM + render" color="amber" />
      </div>

      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="relative flex w-3 h-3">
              <span className={cn('absolute inline-flex h-full w-full rounded-full opacity-75', !paused && 'animate-ping bg-emerald-400')} />
              <span className={cn('relative inline-flex rounded-full h-3 w-3', paused ? 'bg-amber-500' : 'bg-emerald-500')} />
            </span>
            <h2 className="font-display text-lg text-navy-700">Feed en vivo</h2>
            <span className="text-xs text-slate-500">{paused ? 'Pausado' : 'Streaming eventos del copilot'}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => setPaused(p => !p)}>
            {paused ? <><Play className="w-3.5 h-3.5" /> Reanudar</> : <><Pause className="w-3.5 h-3.5" /> Pausar feed</>}
          </Button>
        </header>
        <ul className="divide-y divide-slate-100">
          {eventos.map((ev, i) => {
            const cfg = tipoConfig[ev.tipo]
            const Icon = cfg.icon
            return (
              <li key={ev.id} className={cn('flex items-start gap-3 px-5 py-3 hover:bg-slate-50 transition', i === 0 && !paused && 'bg-emerald-50/40')}>
                <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center shrink-0', cfg.chip)}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Avatar name={ev.cliente} size="sm" />
                    <span className="font-semibold text-navy-700 text-sm">{ev.cliente}</span>
                    <span className="text-xs text-slate-500 font-mono">{ev.wa}</span>
                    <Chip className={cfg.chip}>{cfg.label}</Chip>
                    {ev.intent && <Chip variant="outline" className="font-mono">intent: {ev.intent}</Chip>}
                  </div>
                  <p className="text-sm text-slate-700 mt-1 line-clamp-2">{ev.mensaje}</p>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[11px] text-slate-500 font-mono">{fmtDateTime(ev.fecha).split(' ')[1]}</div>
                  {ev.tipo === 'escalado' && (
                    <Button variant="amber" size="sm" className="mt-1">Ver en cola</Button>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      </section>
    </div>
  )
}
